import { formatDurationAsClock } from 'utils/format_duration'
import { loadTemplate } from "utils/load_template"
import { hideElement, showElement } from 'utils/toggle_element_visibility'
import AudioPlayer from 'utils/audio_player'
import AJAX from 'utils/ajax'
import VideoDataStore from 'models/video_data_store'

export default class AttendanceVerificationManager {
  #checkpoints = []

  constructor(config, modal, log, onFatalError, errorReporter) {
    this.config = config
    this.modal = modal
    this.log = log
    this.#checkpoints = this.buildCheckPoints(this.config.checkpoints)
    this.dataStore = VideoDataStore.getStore(config.videoID)
    this.attendanceCompleted = config.attendanceAlreadyCompleted
    this.attendancePopupCountDownSeconds = config.attendancePopupCountDownSeconds
    this.audioPlayer = new AudioPlayer()
    this.isChimeEnabled = this.audioPlayer.enabled()
    this.onFatalError = onFatalError
    this.errorReporter = errorReporter
    this.dataStore.setAttendanceCheckpoints(this.#checkpoints)

    if(this.isChimeEnabled) {
      this.audioPlayer.setAudioFile(this.config.attendanceVerificationAudioUrl)
    }
  }

  // Builds the checkpoints structure in the new version
  buildCheckPoints(checkpoints) {
    // It has the updated structure
    if (Array.isArray(checkpoints) && checkpoints[0].hasOwnProperty("position")) {
      // Clone the checkpoints passed in, even though they should be in the right format.
      // This helps prevent issues in specs and is generally a better practice anyways.
      return checkpoints.map(e => { return { "position": e.position, "checked_at": e.checked_at } })
    } else {
      return Object.keys(checkpoints).map((secs) => ({ "position": secs, "checked_at": checkpoints[secs].checked_at }))
    }
  }

  uncheckedCheckpoints(){
    return this.#checkpoints.filter((cp) => { return cp.checked_at === null })
  }

  findCheckPoint(seconds) {
    return this.#checkpoints.find(cp => cp.position === seconds)
  }

  // Format the checkpoints without the "shown" attribute which is only used in the client
  checkpointsForRequest() {
    return this.#checkpoints.map((cp) => {
      return {
        position: cp.position,
        checked_at: cp.checked_at
      }
    })
  }

  displayCheckpointPrompt(onDisplay) {
    const node = document.createElement("div")

    node.innerHTML = loadTemplate(this.config.attendanceTemplate)

    onDisplay(node)
  }

  displayExpiredPopup(onDisplay, onClick) {
    const node = document.createElement("div")
    node.innerHTML = loadTemplate(this.config.attendanceExpiredTemplate)

    onDisplay(node)

    const buttonEl = node.querySelector("button")

    buttonEl.addEventListener("click", (_) => {
      onClick(node)
    })
  }

  stopPromptAudio(){
    clearInterval(this.promptAudioInterval)
  }

  startPromptCountDown(node, second, callbacks) {
    const currentCheckpoint = this.findCheckPoint(second)
    const intervalEl = node.querySelector(this.config.attendancePopupCountDownContainerSelector)
    const countdownEndsAt = Date.now() + this.attendancePopupCountDownSeconds * 1000

    currentCheckpoint.shown = true

    if(this.audioPlayer.ready() && this.isChimeEnabled) {
      this.promptAudioInterval = setInterval(() => { this.audioPlayer.play() }, 5000)
    }

    const intervalID = setInterval(() => {
      // We discount 1 second from the count down
      const remainingSeconds = (countdownEndsAt - Date.now()) / 1000

      intervalEl.innerText = formatDurationAsClock(remainingSeconds)

      if (remainingSeconds <= 0) {
        if (!currentCheckpoint.checked_at) {
          this.log(`Missed checkpoint at position ${currentCheckpoint.position}`)
          this.stopPromptAudio()
          callbacks.onClose(node)
          currentCheckpoint.shown = false // So that it can be displayed again
          this.displayExpiredPopup(callbacks.displayExpiredPopup, callbacks.clickExpiredPopup)
        }
        clearInterval(intervalID)
      }
    }, 100)

    this.setCheckpointVerifyButtonBehavior(node, currentCheckpoint, intervalID, callbacks.onClose)
  }

  setCheckpointVerifyButtonBehavior(node, currentCheckpoint, intervalID, onClick) {
    const buttonEl = node.querySelector("button")

    buttonEl.addEventListener("click", async (_) => {
      this.log(`Clicked to mark checkpoint at position ${currentCheckpoint.position}`)
      this.stopPromptAudio()

      buttonEl.disabled = true

      try {
        await this.markCheckpoint(currentCheckpoint)
        currentCheckpoint.shown = false
        onClick(node)
        clearInterval(intervalID)
      } catch(err) {
        this.displayErrorMessage(err)
        this.reportError(err)
      } finally {
        buttonEl.disabled = false
      }
    })
  }

  async markCheckpoint(checkpoint, time) {
    time ||= (new Date()).toISOString()
    checkpoint.checked_at = time
    try {
      await this.submitCheckpointsToServer()
      this.emitCheckpointCheckedEvent(checkpoint)
    } catch(err) {
      checkpoint.checked_at = null
      throw err
    }
  }

  completeAttendance(onSuccess) {
    if(this.attendanceCompleted) { return }

    const missingCheckpoints = this.uncheckedCheckpoints()

    if (missingCheckpoints.length > 0) {
      throw new AttendanceVerificationError(this.checkpoints, missingCheckpoints)
    } else {
      return AJAX.post(
        this.config.attendanceUrl,
        { "attendance": { "completed": true }}
      ).then(() => {
        this.attendanceCompleted = true
        this.emitVideoWatchedEvent()
        onSuccess()
      }).catch(this.onFatalError)
    }
  }

  displayErrorMessage(e) {
    // If the error is related to CSRF we ask the user to sign in again and
    // refresh the page.
    if (e.response.status === 422 && e.response.body.match(/change you wanted was rejected|CSRF/)) {
      this.modal.show("sign-in-form-cle-course")
    } else {
      const $error = document.querySelector(this.config.attendancePopupConnectionErrorContainerSelector)
      showElement($error)

      if (window.innerWidth <= 767) {
        // Hide this element on mobile to make room for error, since space is very limited
        const $instructions = document.querySelector(this.config.attendancePopupInstructionsSelector)
        hideElement($instructions)
      }

      setTimeout(function(){
        hideElement($error)
      }, 5000)
    }
  }

  reportError(err) {
    if(this.errorReporter) {
      this.errorReporter.error(err)
    }
  }

  submitCheckpointsToServer() {
    return AJAX.post(
      this.config.attendanceUrl,
      { "attendance": { "checkpoints": this.checkpointsForRequest() } }
    )
  }

  emitCheckpointCheckedEvent(checkpoint) {
    document.dispatchEvent(new CustomEvent("cle-attendance.checkpointchecked",
      {
        detail: {
          checkpointIndex: this.#checkpoints.indexOf(checkpoint)
        }
      }
    ))
  }

  emitVideoWatchedEvent(){
    document.dispatchEvent(new CustomEvent("cle-attendance.videowatched"))
  }

  get checkpoints() {
    return this.#checkpoints
  }
}

class AttendanceVerificationError extends Error {
  constructor(initialCheckpoints, missingCheckpoints) {
    super("Tried to complete attendance, but there are missing checkpoints.")
    this.initialCheckpoints = initialCheckpoints
    this.missingCheckpoints = missingCheckpoints
  }
}
